import React, { Fragment, useEffect, useRef, useState } from "react";
import AccountProfileSection from "../../components/account/AccountProfileSection";
import AccountTab from "../../components/account/AccountTab";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    bulkRemoveSavedPostsFromFolder,
    deleteSavedVideo,
    getSavedFolders,
    getIndividualFolderData,
} from "../../redux/ApiSlice/SavedVideosSlice";
import { IoArrowBackCircle } from "react-icons/io5";
import { FaFolder, FaTrash } from "react-icons/fa";
import VideoCard from "../../components/common/VideoCard";
import useSelectVideo from "../../hooks/useSelectVideo";
import AccountSidebar from "../../components/account/AccountSidebar";
import AccountTabModal from "../../components/account/AccountTabModal";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import MoveListToFolder from "../../components/Modals/account/MoveListToFolder";
import SkeletonVideoCard from "../../components/skeleton/SkeletonVideoCard";
import { AnimatePresence, motion } from "framer-motion";

export default function SavedFolderData() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const selectVideo = useSelectVideo();
    const { id } = useParams();
    const savedVideo = useSelector((state) => state.savedVideos);
    const { User_profile } = useSelector((state) => state.AccountProfile);
    const { loading, FolderData } = savedVideo;
    const { videos } = FolderData;

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [selectedPostId, setSelectedPostId] = useState(null);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedVideos, setSelectedVideos] = useState([]);

    const [show, setShow] = useState(false);
    const [activeTab, setActiveTab] = useState("");
    const ref = useRef(null);

    const handleClickOut = () => {
        setShow(null);
        setActiveTab("");
    };
    useOutsideClick(ref, handleClickOut);

    const openDrawer = () => setIsDrawerOpen(true);
    const closeDrawer = () => setIsDrawerOpen(false);

    const fetchData = async () => {
        await dispatch(getIndividualFolderData(id));
    };

    const deleteFolderVideo = async (postId) => {
        await dispatch(deleteSavedVideo({ folder_id: id, post_id: postId }));
        fetchData();
    };

    const handleEnterSelectionMode = (postId) => {
        setSelectionMode(true);
        setSelectedVideos([postId]);
    };

    const handleToggleVideoSelection = (postId) => {
        setSelectedVideos((prev) => {
            if (prev.includes(postId)) {
                const newSelection = prev.filter((videoId) => videoId !== postId);
                if (newSelection.length === 0) {
                    setSelectionMode(false);
                }
                return newSelection;
            }
            return [...prev, postId];
        });
    };

    const handleCancelSelection = () => {
        setSelectionMode(false);
        setSelectedVideos([]);
    };

    const handleDeleteSelected = async () => {
        if (selectedVideos.length === 0) return;
        await dispatch(bulkRemoveSavedPostsFromFolder({
            folderId: id,
            postIds: selectedVideos,
        })).unwrap();
        handleCancelSelection();
        fetchData();
    };

    const handleSelectVideo = async (id) => {
        selectVideo(id, videos, location.pathname);
    };

    useEffect(() => {
        fetchData();
        if (User_profile?.id) {
            dispatch(getSavedFolders(User_profile.id));
        }
    }, [dispatch, id, User_profile?.id]);

    const handleMoveVideo = (postId) => {
        setSelectedPostId(postId);
        setSelectedVideos([]);
        setMoveModalOpen(true);
    };

    const handleMoveSelected = () => {
        if (selectedVideos.length === 0) return;
        setSelectedPostId(null);
        setMoveModalOpen(true);
    };

    const handleMoveComplete = () => {
        handleCancelSelection();
        fetchData();
    };

    const videoCount = videos?.length || 0;
    const folderName = FolderData?.name || FolderData?.folder_name || "Saved Folder";

    return (
        <Fragment>
            <div className="bg-[#fff] dark:bg-[#1E1D21] dark:text-white overflow-hidden h-full relative ">
                <AccountProfileSection
                    handleOpenSideBar={openDrawer}
                    handleCloseSideBar={closeDrawer}
                    isDrawerOpen={isDrawerOpen}
                    setActiveTab={setActiveTab}
                    setShow={setShow}
                    show={show}
                />

                <AccountTab activeTab={"Saved"} />

                <div className="flex justify-between items-center px-2">
                    <IoArrowBackCircle
                        onClick={() => navigate('/account/saved', { state: { showFolders: true } })}
                        className="cursor-pointer text-[20px] text-black dark:text-white"
                    />
                    <p className="text-black dark:text-white text-[12px] font-medium truncate px-2">{folderName}</p>
                    <p className="text-black dark:text-white text-[10px]">{videoCount} {videoCount === 1 ? "video" : "videos"}</p>
                </div>

                <div className={`p-2 pt-0 overflow-y-scroll no-scrollbar absolute w-full h-[-webkit-fill-available] ${selectionMode && selectedVideos.length > 0 ? "pb-16" : ""}`}>
                    {loading && (<SkeletonVideoCard />)}
                    {!loading && videos?.length ? (
                        <div className="overflow-y-scroll no-scrollbar">
                            <div className="grid grid-cols-3 gap-1 pt-1 overflow-scroll no-scrollbar">
                                {videos?.map((item) => (
                                    <VideoCard
                                        key={item.id}
                                        belowDecs={true}
                                        threeDots={true}
                                        id={item.id}
                                        video={item.video_url}
                                        likes_count={item.likes_count}
                                        user_id={item.created_by.id}
                                        authorId={item.created_by.id}
                                        avatar_url={item.created_by.avatar_url}
                                        handleDeletePost={deleteFolderVideo}
                                        handleMoveVideo={handleMoveVideo}
                                        handleEnterSelectionMode={handleEnterSelectionMode}
                                        body={item.post_description}
                                        view_count={item.view_count}
                                        selectVideo={handleSelectVideo}
                                        isSavedView={true}
                                        thumbnail={item.thumbnail_url}
                                        showMoveOption={true}
                                        showRemoveOption={true}
                                        removeOptionLabel="Remove"
                                        selectionMode={selectionMode}
                                        isSelected={selectedVideos.includes(item.id)}
                                        onToggleSelection={handleToggleVideoSelection}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-center items-center">
                            <p className="text-[12px] mt-10 text-gray-400 dark:text-gray-500">
                                no saved video yet
                            </p>
                        </div>
                    )}
                </div>

                <AnimatePresence>
                    {selectionMode && selectedVideos.length > 0 && !moveModalOpen && (
                        <motion.div
                            initial={{ y: 100 }}
                            animate={{ y: 0 }}
                            exit={{ y: 100 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="absolute bottom-0 inset-x-0 bg-white dark:bg-[#333333] border-t border-gray-300 dark:border-gray-700 shadow-lg z-50"
                        >
                            <div className="flex items-center justify-between gap-x-2 p-1.5">
                                <button
                                    onClick={handleMoveSelected}
                                    disabled={selectedVideos.length === 0}
                                    className="w-full flex justify-center p-1.5 rounded-md hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FaFolder className="text-[16px] text-gray-700" />
                                </button>
                                <button
                                    onClick={handleDeleteSelected}
                                    disabled={selectedVideos.length === 0}
                                    className="w-full flex justify-center p-1.5 rounded-md hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FaTrash className="text-[16px] text-gray-700" />
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="grid grid-cols-12 overflow-hidden">
                    <div className="col-span-12">
                        <AccountSidebar
                            isDrawerOpen={isDrawerOpen}
                            setIsDrawerOpen={setIsDrawerOpen}
                            openDrawer={openDrawer}
                            closeDrawer={closeDrawer}
                        />
                    </div>
                </div>
            </div>
            {show && (
                <div className={"absolute h-full w-full top-0 overflow-hidden"}>
                    <div
                        className="absolute h-screen w-full top-0 opacity-100 bg-opacity-70 bg-black z-50"
                        onClick={handleClickOut}
                    />
                    <div
                        ref={ref}
                        className="absolute top-0 left-4 z-50 w-[90%] h-[-webkit-fill-available] mt-4 mb-3 flex justify-center rounded-lg  bg-black duration-1000 transform opacity-100"
                    >
                        <AccountTabModal
                            ref={ref}
                            // data={data}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                    </div>
                </div>
            )}

            <MoveListToFolder
                loading={loading}
                selectedPostId={selectedPostId}
                selectedPostIds={selectedVideos}
                SavedFolderList={savedVideo.SavedFolderList}
                isOpen={moveModalOpen}
                handleClose={() => setMoveModalOpen(false)}
                sourceFolderId={id}
                onMoveComplete={handleMoveComplete}
            />
        </Fragment>
    );
}